/**
 * CategoryPicker — THE category control, everywhere one is chosen: click and
 * start typing to filter your categories live (datalist), pick one, or type
 * a brand-new name and it's created on the spot; the shared ✨ AIAction asks
 * AI to suggest from the item's title. Resolves to (id, name) via onPicked, so
 * callers just PATCH the id. Blur or Enter commits.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useId, useRef, useState } from 'react';
import { api } from '../api.js';
import AIAction from './AIAction.jsx';

export default function CategoryPicker({ categories = [], value = null,
                                         title = '', kind = 'expense',
                                         onPicked, className = '' }) {
  const listId = useId();
  const current = categories.find((c) => c.id === Number(value));
  const [text, setText] = useState(current?.name || '');
  const [err, setErr] = useState('');
  // Snapshot of the pick before an AI write, so AIAction's Undo restores it.
  const before = useRef({ id: value, name: current?.name || '' });

  const commit = async (name) => {
    const n = (name || '').trim();
    setErr('');
    try {
      if (!n) { onPicked(null, ''); return; }
      let cat = categories.find((c) => c.name.toLowerCase() === n.toLowerCase());
      if (!cat) {
        cat = await api('/budget/categories/',
                        { method: 'POST', body: { name: n.slice(0, 80) } });
      }
      setText(cat.name);
      onPicked(cat.id, cat.name);
    } catch (e) {
      setErr(e.data?.detail || e.message);
    }
  };

  // The one AI call for this control: suggest a category from the title. Throws
  // a detail-carrying error so AIAction shows the plain-English "type your own".
  const fetchSuggestion = async () => {
    const d = await api('/budget/items/assist/',
                        { method: 'POST', body: { title: title.trim(), kind } });
    if (!d.category) throw { data: { detail: '✨ no confident suggestion — type your own.' } };
    return d;
  };
  const applyResult = (d) => { setText(d.category_name || ''); onPicked(d.category, d.category_name || ''); };

  return (
    <span className={`flex gap-1.5 items-center ${className}`}>
      <input className="input !py-1.5 text-sm flex-1 min-w-0" list={listId}
             placeholder="Category — type, pick, or let ✨ guess"
             title="Start typing: matching categories appear; a new name is created on the spot; ✨ (or focusing while empty) asks AI"
             value={text}
             onChange={(e) => setText(e.target.value)}
             onFocus={() => { if (!text && title.trim()) fetchSuggestion().then(applyResult).catch(() => {}); }}
             onBlur={(e) => commit(e.target.value)}
             onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} />
      <datalist id={listId}>
        {categories.map((c) => <option key={c.id} value={c.name} />)}
      </datalist>
      <AIAction size="sm" label=""
                title="✨ Let AI suggest the category from the name"
                disabled={!title.trim()}
                run={() => { before.current = { id: value, name: text }; return fetchSuggestion(); }}
                onResult={applyResult}
                onUndo={() => { setText(before.current.name); onPicked(before.current.id ?? null, before.current.name); }}
                readback={(d) => `Category: ${d.category_name} — edit before saving.`} />
      {err && <span className="text-[10px] text-vpurple">{err}</span>}
    </span>
  );
}
